import { useDarkMode } from "@/hooks/useDarkMode";
import { useLoadGoogleMaps } from "@/hooks/useLoadGoogleMaps";
import { useSelectedVenue } from "@/hooks/useSelectedVenue";
import {
  leftPanelExpandedAtom,
  mobileListViewAtom,
  savedPlaceIdToViewAtom,
} from "@/state/atoms";
import { GoogleMap, Marker, OverlayView } from "@react-google-maps/api";
import { darkModeStyles, lightModeStyles } from "common/config";
import { Venue, VenuePinType } from "common/types";
import { useAtom } from "jotai";
import React, { useEffect, useRef, useState } from "react";
import GreyPin from "@/assets/pins/grey_pin.svg";
import OrangePin from "@/assets/pins/orange_pin.svg";
import GreyPinSelected from "@/assets/pins/grey_pin_selected.svg";
import OrangePinSelected from "@/assets/pins/orange_pin_selected.svg";
import AnalyticsService from "@/services/analytics";
import VenueCard from "@/components/template/VenueCard";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { FaMinus, FaPlus } from "react-icons/fa6";
import { getPinIcon } from "common/utils/maps";

function MapViewForNearby(props: {
  venueLocation: { latitude: number; longitude: number };
  filteredBusinesses: Venue[];
  venueId: string;
  hidden?: boolean;
}) {
  const [savedPlaceIdToView] = useAtom(savedPlaceIdToViewAtom);

  const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);
  const [isLeftPaneVisible, setIsLeftPaneVisible] = useAtom(
    leftPanelExpandedAtom
  );

  const [activeMarker, setActiveMarker] = useState<string | null>(null);
  const { selectedVenue, setSelectedVenue } = useSelectedVenue();
  const [, setMobileListView] = useAtom(mobileListViewAtom);
  const { isMapsLoaded } = useLoadGoogleMaps();
  const isDark = useDarkMode();
  const markerRefs = useRef(new Map());

  const handleActiveMarker = (
    marker: string,
    newPosition: google.maps.LatLng,
    location: Venue
  ) => {
    if (marker === activeMarker) {
      return;
    }
    setActiveMarker(null);
    setTimeout(() => {
      const overlayHeight = 400; // Adjust according to your overlay's height
      // Calculate the pixel offset to move the map
      const scale = Math.pow(2, mapRef.getZoom());
      const worldCoordinateCenter = mapRef
        .getProjection()
        .fromLatLngToPoint(newPosition);
      const pixelOffset = new google.maps.Point(
        0, // No horizontal shift
        overlayHeight / 2 / scale // Vertical shift
      );

      const newWorldCoordinate = new google.maps.Point(
        worldCoordinateCenter.x - pixelOffset.x,
        worldCoordinateCenter.y - pixelOffset.y
      );

      const newCenter = mapRef
        .getProjection()
        .fromPointToLatLng(newWorldCoordinate);

      mapRef.panTo(newCenter);
      setMobileListView(true);
      setIsLeftPaneVisible(true);
      setActiveMarker(marker);
      setSelectedVenue(location);
    }, 100);
  };

  //result of search api
  // const { locations, isLoading } = useHomeVenues();
  const [delayedLocations, setDelayedLocations] = useState<Venue[]>([]);

  useEffect(() => {
    if (props.filteredBusinesses?.length) {
      const finalLocations =
        props.filteredBusinesses.map((e: Venue) => {
          if (e.venuePinType) return e;
          return {
            ...e,
            venuePinType: getPinIcon(e),
          };
        }) || [];

      // Small delay to ensure map is ready
      setTimeout(() => {
        setDelayedLocations(finalLocations);
      }, 100);
    } else {
      // Clear markers when filteredBusinesses is empty
      setDelayedLocations([]);
    }
  }, [props.filteredBusinesses]);

  const locations = delayedLocations;

  // console.log("Map loaded:", isMapsLoaded);
  // console.log("Locations from props:", locations);
  // console.log("Map ref:", mapRef);

  const onMapMoved = () => {
    // console.log("No progresive search on Nearby Map View");
    return;
  };

  const onLoad = React.useCallback(function callback(map: google.maps.Map) {
    setMapRef(map);
    // Center map on venue location when first loaded
    if (props.venueLocation) {
      map.panTo({
        lat: props.venueLocation.latitude,
        lng: props.venueLocation.longitude,
      });
      map.setZoom(15);
    }
  }, [props.venueLocation]);

  const onUnmount = React.useCallback(function callback() {
    setMapRef(null);
  }, []);

  const showPinIcon = (venue: Venue) => {
    // console.log("showPinIcon venue", venue);
    // console.log("showPinIcon venue.venuePinType", venue.venuePinType);
    if (venue.venuePinType === VenuePinType.Grey) {
      return GreyPin;
    } else {
      return OrangePin;
    }
  };

  const getSelectedPinIcon = (venue: Venue) => {
    // console.log("getSelectedPinIcon venue", venue);
    // console.log("getSelectedPinIcon venue.venuePinType", venue.venuePinType);
    if (venue.venuePinType === VenuePinType.Grey) {
      return GreyPinSelected;
    } else {
      return OrangePinSelected;
    }
  };

  useEffect(() => {
    // Debugging output to ensure locations are received
    // console.log("locations", locations);

    // Initialize pin counters
    let orangePin = 0;
    let greyPin = 0;

    // Iterate over the locations and count pins
    locations.map((location) => {
      if (location.reviewsDetailsSubmitted > 0) {
        orangePin++; // Count orange pin
      } else {
        greyPin++; // Count grey pin
      }
    });

    // Tracking the search event in Google Analytics and Mixpanel
    // console.log("homeSearchParams: ", homeSearchParams);
    AnalyticsService.trackGAEventHandler("nearbyVenuesListSearchPerformed");
    AnalyticsService.trackMx("nearbyVenuesListSearchPerformed", {
      SearchTerm: "",
      totalResults: locations.length,
      orangePin: orangePin,
      greyPin: greyPin,
    });
    // console.log("inside homeSearchPerformed");
  }, []); // Added dependencies for useEffect

  useEffect(() => {
    if (locations.length && mapRef && !activeMarker) {
      const bounds = new google.maps.LatLngBounds();

      // Add all location markers to bounds
      locations.forEach((location) => {
        if (location.latitude && location.longitude) {
          bounds.extend(
            new google.maps.LatLng(location.latitude, location.longitude)
          );
        }
      });

      // Add venue Location if available
      if (props.venueLocation) {
        bounds.extend(
          new google.maps.LatLng(
            props.venueLocation.latitude,
            props.venueLocation.longitude
          )
        );
      }

      // Only fit bounds if we have valid coordinates
      if (!bounds.isEmpty()) {
        mapRef.fitBounds(bounds);
      }
    }
  }, [locations, props.venueLocation, mapRef, activeMarker]);

  // code to show venue marker highlighted
  useEffect(() => {
    if (selectedVenue && savedPlaceIdToView == "" && mapRef) {
      const newPosition = new google.maps.LatLng({
        lat: selectedVenue.latitude,
        lng: selectedVenue.longitude,
      });
      mapRef.panTo(newPosition);
      setActiveMarker(selectedVenue.id);
    } else {
      setActiveMarker(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVenue, mapRef]);

  const mapOptions = {
    keyboardShortcuts: false,
    fullscreenControl: false,
    disableDefaultUI: true,
    ...(isDark
      ? { styles: darkModeStyles } // Add dark mode styles if isDark is true
      : { styles: lightModeStyles }), // Add mapId if isDark is false
  };

  return (
    <div
      className={cn(
        "flex-1 relative h-full rounded-[12px] overflow-clip",
        props.hidden ? "hidden" : "block"
      )}
      aria-label="Map view of nearby venues"
      role="region"
    >
      {isMapsLoaded && (
        <GoogleMap
          mapContainerStyle={{
            width: "100%",
            height: "100%",
          }}
          center={props.venueLocation ? {
            lat: props.venueLocation.latitude,
            lng: props.venueLocation.longitude
          } : undefined}
          zoom={15}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={mapOptions}
          onClick={() => setActiveMarker(null)}
          onIdle={() => onMapMoved()}
          aria-label="Interactive map showing nearby venues"
        >
          {locations.map((location, index) => (
            <Marker
              ref={(marker) => {
                // console.log("Marker data:", {
                //   id: location.id,
                //   lat: location.latitude,
                //   lng: location.longitude,
                //   name: location.name,
                //   selected: selectedVenue?.id === location.id,
                //   index
                // });
                if (marker) {
                  markerRefs.current.set(location.id, marker);
                }
              }}
              key={location.id}
              position={{
                lat: location.latitude,
                lng: location.longitude,
              }}
              title={location.name}
              zIndex={
                selectedVenue && selectedVenue.id === location.id ? 1000 : index
              }
              icon={{
                url:
                  selectedVenue && selectedVenue.id === location.id
                    ? getSelectedPinIcon(location)
                    : showPinIcon(location),
                anchor: new google.maps.Point(15, 30),
                size:
                  selectedVenue && selectedVenue.id === location.id
                    ? new google.maps.Size(40, 40)
                    : new google.maps.Size(30, 30),
                scaledSize:
                  selectedVenue && selectedVenue.id === location.id
                    ? new google.maps.Size(40, 40)
                    : new google.maps.Size(30, 30),
              }}
              onClick={() => {
                AnalyticsService.trackGAEventHandler(
                  "nearbyVenuesListPickedVenueFromSearchResultsMap"
                );
                AnalyticsService.trackMx(
                  "nearbyVenuesListPickedVenueFromSearchResultsMap",
                  {
                    VenueId: location.id,
                    VenueBrandId: location.brandId,
                    VenueBrandName: location.brandName,
                    VenueAddress: location.address,
                    VenueName: location.name,
                    VenueType: location.businessType,
                  }
                );
                const newPosition = new google.maps.LatLng({
                  lat: location.latitude,
                  lng: location.longitude,
                });
                handleActiveMarker(location.id, newPosition, location);
              }}
              options={{
                label: {
                  className: "sr-only",
                  text: `${location.name}, ${location.businessType}, ${location.distance} miles away`,
                }
              }}
            >
              {activeMarker === location.id ? (
                <OverlayView
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  position={
                    (() => {
                      const marker = markerRefs.current?.get(activeMarker);
                      return marker && marker.marker ? 
                        marker.marker.getPosition() as google.maps.LatLng : 
                        new google.maps.LatLng(location.latitude, location.longitude);
                    })()
                  }
                  getPixelPositionOffset={(width, height) => ({
                    x: -(width / 2),
                    y: -(height / 2),
                  })}
                >
                  <div
                    onClick={() => {
                      setSelectedVenue(location);
                    }}
                    className="bg-white dark:bg-primary-900 rounded-lg shadow-md min-w-[300px] translate-x-[-50%] translate-y-[-105%]"
                    role="dialog"
                    aria-label={`Venue details for ${location.name}`}
                  >
                    <VenueCard
                      location={location}
                      onOpen={() => {}}
                      onSaveVenue={() => {}}
                      savedPlaces={[]}
                    />
                  </div>
                </OverlayView>
              ) : null}
            </Marker>
          ))}

        </GoogleMap>
      )}
      {/* Lefbar toggle button */}
      <div
        onClick={() => {
          setIsLeftPaneVisible(!isLeftPaneVisible);
        }}
        style={{
          boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
        }}
        role="button"
        aria-label={isLeftPaneVisible ? "Hide venue list" : "Show venue list"}
        className="hidden md:block absolute left-2 top-2 rounded-[12px] bg-primary-50 dark:bg-dark-800 p-2 z-10 cursor-pointer"
      >
        {isLeftPaneVisible ? (
          <ChevronLeftIcon className="w-6 h-6 text-primary-900 dark:text-primary-50" aria-hidden="true" />
        ) : (
          <ChevronRightIcon className="w-6 h-6 text-primary-900 dark:text-primary-50" aria-hidden="true" />
        )}
      </div>

      {/* Goto current location, +, - */}
      <div className="absolute right-0 bottom-0 top-0 flex flex-col items-end justify-end" aria-label="Map controls">
        <button
          style={{
            boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
          }}
          onClick={() => {
            AnalyticsService.trackGAEventHandler("nearbyVenuesListZoomedInMapView");
            AnalyticsService.trackMx("nearbyVenuesListZoomedInMapView", {});
            mapRef.getZoom() < 17 && mapRef.setZoom(mapRef.getZoom() + 1);
          }}
          aria-label="Zoom in"
          className="p-2 mb-1 mr-2 flex items-center justify-center bg-primary-50 dark:bg-primary-800 rounded-[12px] font-semibold"
        >
          <FaPlus size={22} color={isDark ? "white" : "#121042"} aria-hidden="true" />
        </button>
        <button
          style={{
            boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
          }}
          onClick={() => {
            AnalyticsService.trackGAEventHandler("nearbyVenuesListZoomedOutMapView");
            AnalyticsService.trackMx("nearbyVenuesListZoomedOutMapView", {});
            mapRef.getZoom() > 0 && mapRef.setZoom(mapRef.getZoom() - 1);
          }}
          aria-label="Zoom out"
          className="p-2 mb-8 mr-2 flex items-center justify-center bg-primary-50 dark:bg-primary-800 rounded-[12px] font-semibold"
        >
          <FaMinus size={22} color={isDark ? "white" : "#121042"} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

export default MapViewForNearby;
